import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { useTranslation } from 'react-i18next'
import { MoreHorizontal } from 'lucide-react'
import type { TtsVoice } from '@/domains/types'
import { resolveLanguage } from '@/application/i18n/language'
import { localizedPath } from '@/application/i18n/locale-path'
import type { ReadAloudEngineChoice } from '@/application/stores/read-aloud.store'
import { rankVoices } from '@/application/tts/voice-selection'
import { getVoiceEntry } from '@/application/tts/voice-catalogue'
import { readAloudIconButtonClass } from './readAloudClasses'

const AUTOMATIC_VALUE = '__automatic__'

interface ReadAloudVoiceMenuProps {
  /** Every voice the browser reports, unfiltered — this menu narrows it down
   * to `primaryLang` itself. */
  voices: TtsVoice[]
  primaryLang: string
  /** The visitor's explicit choice, or `null` for "Automatic". */
  selectedVoiceId: string | null
  onSelectVoice: (voiceId: string | null) => void
  lowQualityOnly: boolean
  /** Which engine the visitor picked. */
  enginePreference?: ReadAloudEngineChoice
  /** The site (Piper) voices can run in this browser — when false the menu
   * is the v1 device-voice menu only. */
  siteAvailable?: boolean
  /** The browser has a device voice for the article's language. */
  deviceAvailable?: boolean
  /** Site voices failed this session; device voices are speaking instead. */
  usingFallback?: boolean
  onSelectEngine?: (choice: ReadAloudEngineChoice) => void
}

const ENGINE_ITEM_CLASS =
  'cursor-pointer select-none rounded px-2 py-1.5 text-sm text-foreground outline-none data-[highlighted]:bg-accent data-[state=checked]:font-semibold data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'

/**
 * The "⋯" playback-options menu: a "Voice" radio group listing "Automatic"
 * plus every voice available for the article's own (title) language,
 * ranked best-first by `rankVoices` — the same ranking `selectVoice` uses
 * to pick "Automatic"'s actual voice, so the ordering here matches what
 * picking "Automatic" would actually choose.
 */
export function ReadAloudVoiceMenu({
  voices,
  primaryLang,
  selectedVoiceId,
  onSelectVoice,
  lowQualityOnly,
  enginePreference = 'device',
  siteAvailable = false,
  deviceAvailable = true,
  usingFallback = false,
  onSelectEngine = () => {},
}: ReadAloudVoiceMenuProps) {
  const { t, i18n } = useTranslation()
  const showSiteVoice = siteAvailable && enginePreference === 'site'
  const showDeviceVoices = !showSiteVoice
  const siteVoice = getVoiceEntry(resolveLanguage(primaryLang))
  const creditsHref = localizedPath('/credits-audio', resolveLanguage(i18n.language))
  const navigatorLanguages =
    typeof navigator !== 'undefined' && navigator.languages ? navigator.languages : []
  const rankedVoices = rankVoices(voices, primaryLang, navigatorLanguages)

  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={t('read_mode.listen.more_options')}
          title={t('read_mode.listen.more_options')}
          className={readAloudIconButtonClass}
        >
          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align="end"
          sideOffset={8}
          className="z-50 w-64 rounded-lg border border-input bg-background p-1 shadow-lg print:hidden"
        >
          {siteAvailable && (
            <>
              <DropdownMenuPrimitive.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('read_mode.listen.engine_label')}
              </DropdownMenuPrimitive.Label>
              <DropdownMenuPrimitive.RadioGroup
                value={enginePreference}
                onValueChange={(value) => {
                  if (value === 'site' || value === 'device') onSelectEngine(value)
                }}
              >
                <DropdownMenuPrimitive.RadioItem value="site" className={ENGINE_ITEM_CLASS}>
                  {t('read_mode.listen.engine_site')}
                </DropdownMenuPrimitive.RadioItem>
                <DropdownMenuPrimitive.RadioItem
                  value="device"
                  disabled={!deviceAvailable}
                  className={ENGINE_ITEM_CLASS}
                >
                  {t('read_mode.listen.engine_device')}
                </DropdownMenuPrimitive.RadioItem>
              </DropdownMenuPrimitive.RadioGroup>
              {!deviceAvailable && (
                <p className="px-2 pb-1 text-xs text-muted-foreground">
                  {t('read_mode.listen.engine_device_unavailable')}
                </p>
              )}
              <DropdownMenuPrimitive.Separator className="my-1 h-px bg-border" />
            </>
          )}
          {showSiteVoice && (
            <div className="px-2 py-1.5 text-sm text-foreground">
              <p className="font-semibold">
                {usingFallback
                  ? t('read_mode.listen.active_voice_device')
                  : t('read_mode.listen.active_voice', { name: siteVoice.displayName })}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {siteVoice.attribution} — {siteVoice.license}
              </p>
            </div>
          )}
          {showDeviceVoices && (
            <>
              <DropdownMenuPrimitive.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('read_mode.listen.voice_label')}
              </DropdownMenuPrimitive.Label>
              <DropdownMenuPrimitive.RadioGroup
                value={selectedVoiceId ?? AUTOMATIC_VALUE}
                onValueChange={(value) => onSelectVoice(value === AUTOMATIC_VALUE ? null : value)}
              >
                <DropdownMenuPrimitive.RadioItem
                  value={AUTOMATIC_VALUE}
                  className="cursor-pointer select-none rounded px-2 py-1.5 text-sm text-foreground outline-none data-[highlighted]:bg-accent data-[state=checked]:font-semibold"
                >
                  {t('read_mode.listen.voice_auto')}
                </DropdownMenuPrimitive.RadioItem>
                {rankedVoices.map((voice) => (
                  <DropdownMenuPrimitive.RadioItem
                    key={voice.id}
                    value={voice.id}
                    className="cursor-pointer select-none rounded px-2 py-1.5 text-sm text-foreground outline-none data-[highlighted]:bg-accent data-[state=checked]:font-semibold"
                  >
                    {voice.name}
                    {voice.quality === 'high' && (
                      <span className="ml-1.5 text-xs text-muted-foreground">
                        {t('read_mode.listen.voice_quality_high')}
                      </span>
                    )}
                  </DropdownMenuPrimitive.RadioItem>
                ))}
              </DropdownMenuPrimitive.RadioGroup>
              {lowQualityOnly && (
                <p className="mt-1 border-t border-border px-2 pt-2 text-xs text-muted-foreground">
                  {t('read_mode.listen.voice_low_quality_hint')}
                </p>
              )}
            </>
          )}
          {siteAvailable && (
            <>
              <DropdownMenuPrimitive.Separator className="my-1 h-px bg-border" />
              <DropdownMenuPrimitive.Item asChild>
                <a
                  href={creditsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block cursor-pointer rounded px-2 py-1.5 text-sm text-foreground underline underline-offset-2 outline-none data-[highlighted]:bg-accent"
                >
                  {t('read_mode.listen.credits_link')}
                </a>
              </DropdownMenuPrimitive.Item>
            </>
          )}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  )
}
